import React from 'react';
import { useSelector } from 'react-redux';
import { resolveTheme } from '../utils/themeResolver';
import { useDesignV2 } from '../utils/designV2';
import GoatMark from './GoatMark';

const MixGoatLogo = ({ width = 40, height = 40, variant = 'default' }) => {
  const theme = useSelector((state) => state.settings.theme);
  const v2 = useDesignV2();
  const isLight = resolveTheme(theme) === 'light';
  // Design v2 wears the new goat mark everywhere the logo appears.
  if (v2) return <GoatMark size={width} />;
  const goatColor = isLight ? '#00bcd4' : '#ffffff';
  const headphoneColor = isLight ? '#1a1a1a' : '#00bcd4';
  const eyeColor = isLight ? '#ffffff' : '#0a0a0a';

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 512 512"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Goat head */}
      <ellipse cx="256" cy="295" rx="143" ry="163" fill={goatColor}/>

      {/* Left horn */}
      <path d="M140 185 Q80 80 122 40" stroke={goatColor} strokeWidth="42" strokeLinecap="round" fill="none"/>

      {/* Right horn */}
      <path d="M372 185 Q432 80 390 40" stroke={goatColor} strokeWidth="42" strokeLinecap="round" fill="none"/>

      {/* Headphone band */}
      <path d="M76 250 Q76 120 256 100 Q436 120 436 250" stroke={headphoneColor} strokeWidth="32" fill="none" strokeLinecap="round"/>

      {/* Left headphone cup */}
      <rect x="38" y="225" width="85" height="125" rx="20" fill={headphoneColor}/>

      {/* Right headphone cup */}
      <rect x="389" y="225" width="85" height="125" rx="20" fill={headphoneColor}/>

      {/* Left eye */}
      <circle cx="200" cy="270" r="22" fill={eyeColor}/>

      {/* Right eye */}
      <circle cx="312" cy="270" r="22" fill={eyeColor}/>
    </svg>
  );
};

// Wordmark version with text
export const MixGoatWordmark = ({ height = 50 }) => {
  const theme = useSelector((state) => state.settings.theme);
  const v2 = useDesignV2();
  const isLight = resolveTheme(theme) === 'light';
  // Design v2: the sidebar's wordmark at a larger size — amber goat, ink "Mix",
  // amber "Goat", Inter (classic's cyan Arial "Goat" sat next to the amber goat).
  if (v2) {
    return (
      <div className="v2-wordmark" style={{ display: 'flex', alignItems: 'center', gap: `${Math.round(height * 0.2)}px` }}>
        <GoatMark size={Math.round(height * 0.8)} />
        <span style={{ font: `700 ${Math.round(height * 0.5)}px/1 var(--v2-font)`, letterSpacing: '-.02em', color: 'var(--color-text-primary)' }}>
          Mix<span style={{ color: 'var(--color-accent)' }}>Goat</span>
        </span>
      </div>
    );
  }
  const accentColor = '#00bcd4';
  const mixColor = isLight ? '#1a1a1a' : '#ffffff';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <MixGoatLogo width={height} height={height} />
      <span
        style={{
          fontSize: `${height * 0.6}px`,
          fontWeight: 'bold',
          color: mixColor,
          fontFamily: 'Arial, sans-serif',
          letterSpacing: '0.5px'
        }}
      >
        Mix<span style={{ color: accentColor }}>Goat</span>
      </span>
    </div>
  );
};

// App bar version
export const MixGoatAppBar = () => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '8px 16px',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
        borderBottom: '1px solid rgba(0, 188, 212, 0.2)'
      }}
    >
      <MixGoatWordmark height={36} />
    </div>
  );
};

export default MixGoatLogo;
